import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import { useAccount, useWalletClient, useSwitchChain } from 'wagmi';
import { decodeEventLog } from 'viem';
import { supabase, STORAGE_BUCKETS, uploadBase64ImageToStorage } from '../../utils/supabase';
import { mainnet } from 'viem/chains';

// Ethereum Brand Colors
const ETH_BLUE = '#627EEA';

// Ethereum Token Factory Contract
const ETH_FACTORY_ADDRESS = '0x1D2c499996517b958fF715C8FBCcC631B2fDCB9B';
const ETH_CHAIN_ID = 1;

// Factory ABI - matching deployed Ethereum contract
const FACTORY_ABI = [
    { "inputs": [{ "internalType": "address", "name": "_poolManager", "type": "address" }], "stateMutability": "nonpayable", "type": "constructor" },
    { "anonymous": false, "inputs": [{ "indexed": true, "internalType": "uint256", "name": "tokenId", "type": "uint256" }, { "indexed": false, "internalType": "address", "name": "recipient", "type": "address" }, { "indexed": false, "internalType": "uint256", "name": "amount", "type": "uint256" }, { "indexed": false, "internalType": "string", "name": "distributionType", "type": "string" }], "name": "FeeDistributed", "type": "event" },
    { "anonymous": false, "inputs": [{ "indexed": true, "internalType": "uint256", "name": "tokenId", "type": "uint256" }, { "indexed": false, "internalType": "uint256", "name": "amount0", "type": "uint256" }, { "indexed": false, "internalType": "uint256", "name": "amount1", "type": "uint256" }], "name": "FeesCollected", "type": "event" },
    { "anonymous": false, "inputs": [{ "indexed": false, "internalType": "uint256", "name": "tokenId", "type": "uint256" }, { "indexed": true, "internalType": "address", "name": "creator", "type": "address" }], "name": "LiquidityMinted", "type": "event" },
    { "anonymous": false, "inputs": [{ "indexed": false, "internalType": "address", "name": "pool", "type": "address" }], "name": "PoolInitialized", "type": "event" },
    { "anonymous": false, "inputs": [{ "indexed": false, "internalType": "address", "name": "oldManager", "type": "address" }, { "indexed": false, "internalType": "address", "name": "newManager", "type": "address" }], "name": "PoolManagerUpdated", "type": "event" },
    { "anonymous": false, "inputs": [{ "indexed": true, "internalType": "address", "name": "tokenAddress", "type": "address" }, { "indexed": false, "internalType": "string", "name": "name", "type": "string" }, { "indexed": false, "internalType": "string", "name": "symbol", "type": "string" }, { "indexed": true, "internalType": "address", "name": "creator", "type": "address" }, { "indexed": false, "internalType": "uint256", "name": "totalDeployed", "type": "uint256" }], "name": "TokenDeployed", "type": "event" },
    { "inputs": [], "name": "CREATOR_SPLIT_BPS", "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }], "stateMutability": "view", "type": "function" },
    { "inputs": [], "name": "DEPLOYER_SPLIT_BPS", "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }], "stateMutability": "view", "type": "function" },
    { "inputs": [], "name": "FACTORY_DEPLOYER", "outputs": [{ "internalType": "address", "name": "", "type": "address" }], "stateMutability": "view", "type": "function" },
    { "inputs": [], "name": "FEE_TIER", "outputs": [{ "internalType": "uint24", "name": "", "type": "uint24" }], "stateMutability": "view", "type": "function" },
    { "inputs": [], "name": "NONFUNGIBLE_POSITION_MANAGER", "outputs": [{ "internalType": "address", "name": "", "type": "address" }], "stateMutability": "view", "type": "function" },
    { "inputs": [], "name": "UNISWAP_V3_FACTORY", "outputs": [{ "internalType": "address", "name": "", "type": "address" }], "stateMutability": "view", "type": "function" },
    { "inputs": [], "name": "WETH", "outputs": [{ "internalType": "address", "name": "", "type": "address" }], "stateMutability": "view", "type": "function" },
    { "inputs": [{ "internalType": "uint256", "name": "tokenId", "type": "uint256" }], "name": "collectFees", "outputs": [], "stateMutability": "nonpayable", "type": "function" },
    { "inputs": [{ "internalType": "address", "name": "", "type": "address" }, { "internalType": "uint256", "name": "", "type": "uint256" }], "name": "creatorNFTs", "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }], "stateMutability": "view", "type": "function" },
    { "inputs": [{ "internalType": "address", "name": "", "type": "address" }], "name": "creatorTokenCount", "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }], "stateMutability": "view", "type": "function" },
    { "inputs": [{ "internalType": "address", "name": "creator", "type": "address" }], "name": "getCreatorNFTCount", "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }], "stateMutability": "view", "type": "function" },
    { "inputs": [{ "internalType": "address", "name": "creator", "type": "address" }], "name": "getCreatorNFTs", "outputs": [{ "internalType": "uint256[]", "name": "", "type": "uint256[]" }], "stateMutability": "view", "type": "function" },
    { "inputs": [{ "internalType": "address", "name": "creator", "type": "address" }], "name": "getCreatorTokenCount", "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }], "stateMutability": "view", "type": "function" },
    { "inputs": [{ "internalType": "uint256", "name": "tokenId", "type": "uint256" }], "name": "getNFTCreator", "outputs": [{ "internalType": "address", "name": "", "type": "address" }], "stateMutability": "view", "type": "function" },
    { "inputs": [], "name": "getPoolManager", "outputs": [{ "internalType": "address", "name": "", "type": "address" }], "stateMutability": "view", "type": "function" },
    { "inputs": [], "name": "getTotalTokensDeployed", "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }], "stateMutability": "view", "type": "function" },
    { "inputs": [], "name": "mavrkPoolManager", "outputs": [{ "internalType": "address", "name": "", "type": "address" }], "stateMutability": "view", "type": "function" },
    { "inputs": [{ "internalType": "string", "name": "_name", "type": "string" }, { "internalType": "string", "name": "_symbol", "type": "string" }], "name": "newMavrkToken", "outputs": [], "stateMutability": "nonpayable", "type": "function" },
    { "inputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }], "name": "nftCreators", "outputs": [{ "internalType": "address", "name": "", "type": "address" }], "stateMutability": "view", "type": "function" },
    { "inputs": [], "name": "owner", "outputs": [{ "internalType": "address", "name": "", "type": "address" }], "stateMutability": "view", "type": "function" },
    { "inputs": [{ "internalType": "address", "name": "newPoolManager", "type": "address" }], "name": "setPoolManager", "outputs": [], "stateMutability": "nonpayable", "type": "function" },
    { "inputs": [{ "internalType": "address", "name": "", "type": "address" }], "name": "totalClaimedFees", "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }], "stateMutability": "view", "type": "function" },
    { "inputs": [{ "internalType": "address", "name": "", "type": "address" }], "name": "totalCreatorFees", "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }], "stateMutability": "view", "type": "function" },
    { "inputs": [{ "internalType": "address", "name": "", "type": "address" }], "name": "totalDeployerFees", "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }], "stateMutability": "view", "type": "function" },
    { "inputs": [], "name": "totalTokensDeployed", "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }], "stateMutability": "view", "type": "function" },
    { "inputs": [{ "internalType": "address", "name": "newOwner", "type": "address" }], "name": "transferOwnership", "outputs": [], "stateMutability": "nonpayable", "type": "function" }
] as const;

interface CreateEthTokenModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: #000000;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 32px;
  box-sizing: border-box;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 700px) {
    padding: 40px 24px 24px;
    max-width: 95%;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  color: #FFFFFF !important;
  font-size: 32px;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: rotate(90deg);
  }
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #FFFFFF !important;
  font-family: 'Inter', sans-serif;
  margin: 12px 0 24px 0;
  text-align: center;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #FFFFFF !important;
  font-family: 'Inter', sans-serif;
  margin-bottom: 8px;
  
  .required {
    color: #ff4444 !important;
  }
`;

const Input = styled.input`
  width: 100%;
  background: #1A1A1A;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0;
  padding: 12px 16px;
  color: #FFFFFF !important;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  box-sizing: border-box;
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    background: #252525;
    border-color: ${ETH_BLUE};
    box-shadow: 0 0 0 3px rgba(98, 126, 234, 0.1);
  }

  &::placeholder {
    color: #666666 !important;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  display: block;
  width: 100%;
  background: #1A1A1A;
  border: 2px dashed rgba(255, 255, 255, 0.1);
  border-radius: 0;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #999999 !important;
  font-family: 'Inter', sans-serif;
  font-size: 14px;

  &:hover {
    background: #252525;
    border-color: ${ETH_BLUE};
    color: ${ETH_BLUE} !important;
  }
`;

const LogoPreview = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 0;
  object-fit: cover;
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin: 12px auto;
  display: block;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

const Button = styled.button<{ disabled?: boolean; variant?: 'primary' | 'secondary' }>`
  width: 100%;
  padding: 14px 24px;
  background: ${props =>
        props.disabled
            ? '#333333'
            : props.variant === 'secondary'
                ? '#1A1A1A'
                : ETH_BLUE};
  border: none;
  border-radius: 0;
  color: ${props =>
        props.disabled
            ? '#666666'
            : '#FFFFFF'} !important;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  margin-top: 8px;
  box-shadow: ${props => props.disabled || props.variant === 'secondary' ? 'none' : '0 4px 12px rgba(98, 126, 234, 0.3)'};

  &:hover:not(:disabled) {
    background: ${props => props.variant === 'secondary'
        ? '#333333'
        : '#5370E0'};
    transform: translateY(-2px);
    box-shadow: ${props => props.variant === 'secondary' ? 'none' : '0 6px 16px rgba(98, 126, 234, 0.4)'};
  }
`;

const StatusMessage = styled.div<{ type: 'info' | 'success' | 'error' }>`
  padding: 12px 16px;
  border-radius: 0;
  margin-bottom: 20px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  text-align: center;
  background: ${props =>
        props.type === 'error' ? 'rgba(255, 68, 68, 0.1)' :
            props.type === 'success' ? 'rgba(16, 185, 129, 0.1)' :
                'rgba(98, 126, 234, 0.1)'};
  color: ${props =>
        props.type === 'error' ? '#ff4444' :
            props.type === 'success' ? '#10b981' :
                ETH_BLUE} !important;
  border: 1px solid ${props =>
        props.type === 'error' ? 'rgba(255, 68, 68, 0.2)' :
            props.type === 'success' ? 'rgba(16, 185, 129, 0.2)' :
                'rgba(98, 126, 234, 0.2)'};
`;

const CreateEthTokenModal: React.FC<CreateEthTokenModalProps> = ({
    isOpen,
    onClose,
    onSuccess
}) => {
    const { address, isConnected } = useAccount();
    const { data: walletClient } = useWalletClient();
    const { switchChain } = useSwitchChain();

    const [formData, setFormData] = useState({
        name: '',
        symbol: ''
    });

    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string>('');
    const [status, setStatus] = useState<{ type: 'info' | 'success' | 'error'; message: string } | null>(null);
    const [isDeploying, setIsDeploying] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Helper to resize image
    const resizeImage = (file: File, maxSize: number): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    if (width > height) {
                        if (width > maxSize) {
                            height = (height * maxSize) / width;
                            width = maxSize;
                        }
                    } else {
                        if (height > maxSize) {
                            width = (width * maxSize) / height;
                            height = maxSize;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        reject(new Error('Could not get canvas context'));
                        return;
                    }
                    ctx.drawImage(img, 0, 0, width, height);
                    resolve(canvas.toDataURL('image/png'));
                };
                img.onerror = reject;
                img.src = e.target?.result as string;
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setLogoFile(file);
            const preview = await resizeImage(file, 512);
            setLogoPreview(preview);
        }
    };

    const handleDeploy = async () => {
        if (!isConnected || !address) {
            setStatus({ type: 'error', message: 'Please connect your wallet first' });
            return;
        }

        if (!walletClient) {
            setStatus({ type: 'error', message: 'Wallet client not available' });
            return;
        }

        if (!formData.name || !formData.symbol) {
            setStatus({ type: 'error', message: 'Name and symbol are required' });
            return;
        }

        if (!logoFile) {
            setStatus({ type: 'error', message: 'Logo is required' });
            return;
        }

        setIsDeploying(true);
        setStatus({ type: 'info', message: 'Switching to Ethereum Mainnet...' });

        try {
            // Switch to Ethereum Mainnet
            const currentChainId = await walletClient.getChainId();
            if (currentChainId !== ETH_CHAIN_ID) {
                await switchChain({ chainId: ETH_CHAIN_ID });
                await new Promise(resolve => setTimeout(resolve, 2000)); // Wait for chain switch
            }

            setStatus({ type: 'info', message: 'Deploying token contract...' });

            // Prepare logo for upload
            const logoBase64 = await resizeImage(logoFile, 512);

            // Deploy token using wagmi writeContract
            const hash = await walletClient.writeContract({
                address: ETH_FACTORY_ADDRESS,
                abi: FACTORY_ABI,
                functionName: 'newMavrkToken',
                args: [formData.name, formData.symbol],
                // No value needed as function is nonpayable
            });

            setStatus({ type: 'info', message: 'Waiting for transaction confirmation...' });

            // Wait for transaction receipt
            const { createPublicClient, http } = await import('viem');
            const ethPublicClient = createPublicClient({
                chain: mainnet,
                transport: http()
            });

            const receipt = await ethPublicClient.waitForTransactionReceipt({ hash });

            if (receipt.status !== 'success') {
                throw new Error('Transaction failed');
            }

            setStatus({ type: 'info', message: 'Parsing transaction logs...' });

            // Parse logs
            let deployedTokenAddress: string | null = null;
            let poolAddress: string | null = null;
            let nftTokenId: string | null = null;

            for (const log of receipt.logs) {
                try {
                    // Skip logs without topics
                    if (!log.topics || log.topics.length === 0) {
                        continue;
                    }

                    // Check if log is from our factory contract
                    if (log.address?.toLowerCase() !== ETH_FACTORY_ADDRESS.toLowerCase()) {
                        continue;
                    }

                    // Try to decode event
                    const decoded = decodeEventLog({
                        abi: FACTORY_ABI,
                        data: log.data,
                        topics: log.topics as [signature: `0x${string}`, ...args: `0x${string}`[]]
                    });

                    if (decoded.eventName === 'TokenDeployed' && decoded.args) {
                        deployedTokenAddress = decoded.args.tokenAddress as string;
                        console.log('✅ Token deployed:', deployedTokenAddress);
                    } else if (decoded.eventName === 'PoolInitialized' && decoded.args) {
                        poolAddress = decoded.args.pool as string;
                        console.log('✅ Pool initialized:', poolAddress);
                    } else if (decoded.eventName === 'LiquidityMinted' && decoded.args) {
                        nftTokenId = decoded.args.tokenId.toString();
                        console.log('✅ Liquidity minted, NFT ID:', nftTokenId);
                    }
                } catch (e) {
                    console.warn('Failed to decode log:', e);
                }
            }

            if (!deployedTokenAddress) {
                throw new Error(
                    `Could not determine token address from transaction logs. ` +
                    `Transaction hash: ${hash}. `
                );
            }

            setStatus({ type: 'info', message: 'Uploading logo to storage...' });

            // Upload logo to Supabase
            const logoFileName = `${deployedTokenAddress}-logo.png`;
            const logoUrl = await uploadBase64ImageToStorage(
                logoBase64.split(',')[1], // Remove data:image/png;base64, prefix
                STORAGE_BUCKETS.ETH_LOGOS,
                logoFileName
            );

            if (!logoUrl) {
                throw new Error('Failed to upload logo');
            }

            setStatus({ type: 'info', message: 'Saving token to database...' });

            // Save token to tokens_eth table
            const { error: insertError } = await supabase
                .from('tokens_eth')
                .insert({
                    name: formData.name,
                    symbol: formData.symbol,
                    logo_url: logoUrl,
                    contract_address: deployedTokenAddress,
                    network: 'eth',
                    pool_address: poolAddress,
                    nft_token_id: nftTokenId,
                    deployer_address: address.toLowerCase()
                });

            if (insertError) {
                throw new Error(`Failed to save token: ${insertError.message}`);
            }

            setStatus({
                type: 'success',
                message: `Token deployed successfully! Address: ${deployedTokenAddress.slice(0, 6)}...${deployedTokenAddress.slice(-4)}`
            });

            // Reset form
            setFormData({
                name: '',
                symbol: ''
            });
            setLogoFile(null);
            setLogoPreview('');
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            // Call success callback
            if (onSuccess) {
                setTimeout(() => {
                    onSuccess();
                    onClose();
                }, 2000);
            } else {
                setTimeout(() => {
                    onClose();
                }, 2000);
            }

        } catch (error: any) {
            console.error('Deployment error:', error);
            setStatus({
                type: 'error',
                message: error.message || 'Deployment failed. Please try again.'
            });
        } finally {
            setIsDeploying(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="Create Ethereum Token"
            ariaHideApp={false}
            style={{
                overlay: {
                    backgroundColor: 'rgba(0,0,0,0.85)',
                    zIndex: 1000,
                    backdropFilter: 'blur(8px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                },
                content: {
                    maxWidth: '600px',
                    margin: 'auto',
                    borderRadius: '0',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    inset: 'auto',
                    position: 'relative'
                },
            }}
        >
            <ModalContent>
                <CloseButton onClick={onClose}>×</CloseButton>
                <Title>Create Ethereum Token</Title>

                {status && (
                    <StatusMessage type={status.type}>{status.message}</StatusMessage>
                )}

                <FormGroup>
                    <Label>
                        Token Name <span className="required">*</span>
                    </Label>
                    <Input
                        type="text"
                        placeholder="My Token"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        disabled={isDeploying}
                    />
                </FormGroup>

                <FormGroup>
                    <Label>
                        Token Symbol <span className="required">*</span>
                    </Label>
                    <Input
                        type="text"
                        placeholder="ETH"
                        value={formData.symbol}
                        onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
                        disabled={isDeploying}
                        maxLength={10}
                    />
                </FormGroup>

                <FormGroup>
                    <Label>Logo <span className="required">*</span></Label>
                    <FileInput
                        ref={fileInputRef}
                        id="logo-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        disabled={isDeploying}
                    />
                    <FileInputLabel htmlFor="logo-upload" onClick={(e) => {
                        if (!isDeploying) {
                            fileInputRef.current?.click();
                        }
                        e.preventDefault();
                    }}>
                        {logoPreview ? (
                            <>
                                <LogoPreview src={logoPreview} alt="Logo preview" />
                                <div>Click to change logo</div>
                            </>
                        ) : (
                            'Click to upload logo (PNG, JPG, etc.)'
                        )}
                    </FileInputLabel>
                </FormGroup>

                <Button onClick={handleDeploy} disabled={isDeploying}>
                    {isDeploying ? 'Deploying...' : 'Deploy Token (1 Gwei)'}
                </Button>
            </ModalContent>
        </Modal>
    );
};

export default CreateEthTokenModal;
